import { useAuthStore } from "../auth/auth_store";
import { useUIStore } from "../store/ui";
import { getSurfaceToken } from "./surface_token";
import { CLIENT_VERSION_STRING } from "../types/wire";
import { reachable } from "../lib/format/network_error";

/**
 * Support-correlation Response shape. The raw `Response`
 * returned by `fetch` gets an extra `traceId` getter attached
 * in-place via `Object.defineProperty` so existing callers'
 * `.ok` / `.status` / `.json()` paths run verbatim — no
 * body-stream re-wrap, no subclass constructor. `traceId`
 * returns the 32-char hex `x-trace-id` header value when the
 * server attached one, `null` otherwise. Mirrors the
 * PokerStars / GGPoker / partypoker CS-correlation convention.
 *
 * Minimal-caller-migration design: the type intersection
 * (`Response & { traceId }`) keeps the call site's existing
 * return-type assignability intact, so each error-path caller
 * picks up the suffix via a single-line `response.traceId`
 * pass to `formatServerError`. No throw / shape change.
 */
export type TracedResponse = Response & {
  readonly traceId: string | null;
};

function parseTraceIdHeader(raw: Response): string | null {
  // Test mocks return plain-object stubs without a `.headers`
  // field — defensively handle the absent / non-Headers case
  // so the helper never tears down a 200 / 400 / 402 path that
  // a unit test exercises against a hand-rolled stub. The
  // production path always carries a real `Headers` instance.
  const h = raw.headers as Headers | undefined;
  if (h === undefined || typeof h.get !== "function") {
    return null;
  }
  const v = h.get("x-trace-id");
  // Spec §4.1 — 32-char hex (Uuid v4 simple). A header value
  // that does not match the shape is treated as absent so a
  // future proxy that rewrites the header never surfaces a
  // malformed id in the toast.
  if (v !== null && /^[0-9a-f]{32}$/i.test(v)) {
    return v;
  }
  return null;
}

function attachTraceId(raw: Response): TracedResponse {
  // Attach the `traceId` getter directly to the raw Response
  // (no body-stream re-wrap). Non-enumerable so JSON.stringify
  // / serialize paths never accidentally pick it up.
  const traceId = parseTraceIdHeader(raw);
  Object.defineProperty(raw, "traceId", {
    value: traceId,
    enumerable: false,
    writable: false,
    configurable: false,
  });
  return raw as TracedResponse;
}

/**
 * Shared wrapper for every
 * authed HTTP surface. Four responsibilities:
 *
 *   1. **Cookie auto-attach** — `credentials: 'include'` on every
 *      request so the BE-issued `arkhe_player_session` cookie rides
 *      back. Mirrors the OWASP Session Management Cheat Sheet
 *      §1.2.3 default + PokerStars / GGPoker / partypoker / 888
 *      same-origin cookie convention.
 *   2. **CSRF Double-Submit attach** — every mutating verb gets
 *      `x-csrf-token` echoed back to satisfy the server-side
 *      constant-time compare against `account_sessions.csrf_token`
 *      (ADR-010). Read at call-time from the auth slice so a
 *      future rotate (`setCsrfToken`) takes effect on the next
 *      request without a remount. GET / HEAD pass through bare
 *      since the BE only validates the header on mutating routes.
 *   3. **Revoked-session silent trigger** — when the BE returns
 *      401 or 403 with one of the four canonical session-revoked
 *      i18n keys, the helper fires `auth_store.logout()`. That
 *      wipes the signing key + cookie + drops the user on the
 *      LoginScreen via the AuthGate's `signingKey === null`
 *      branch. The caller still receives the raw `Response` so
 *      its standard error path can also surface a toast if
 *      desired (the silent trigger is additive, not replacement).
 *      Idempotent: `logout()` re-entry against an already-wiped
 *      slice is a no-op aside from a second fire-and-forget
 *      `POST /accounts/logout` (BE-side idempotent per
 *      `accounts_logout` contract).
 *   4. **429 auto-retry with exponential backoff + jitter**
 *      (SERVER-HYGIENE-INTEGRATED sub-3). When the BE's
 *      rate-limit gate emits `429 Too Many Requests`, the
 *      helper honours `Retry-After` (RFC 6585 §4 — seconds OR
 *      HTTP-date) + backs off via the standard PokerStars /
 *      GGPoker schedule: `delay = min(base × 2^attempt,
 *      Retry-After × 1000) × jitter(0.8-1.2)`. Up to 3 retry
 *      attempts; on exhaustion the final 429 response is
 *      returned for the caller's standard error path to
 *      render `error.too_many_requests`. `base = 500ms`. Body
 *      replay supports `string | URLSearchParams | Uint8Array
 *      | FormData | Blob`; a `ReadableStream` body cannot be
 *      safely re-played + throws `RetryBodyUnsupported`
 *      synchronously before the first request — defensive
 *      guard, the current FE never streams bodies.
 *
 * The helper deliberately does NOT swallow non-revoked 4xx / 5xx —
 * the existing per-endpoint error parsing in the callers stays
 * the source of truth for everything outside the session class.
 */

/** Wire `i18n_key` values the BE attaches to the four 401/403
 *  reason classes the spec lumps into "session has been revoked,
 *  log in again". Matches:
 *   - `auth.missing_session_token` (401, cookie absent)
 *   - `auth.session_token_invalid` (401, cookie can't decode)
 *   - `auth.session_revoked` (401, row revoked or expired)
 *   - `auth.csrf_invalid` (403, csrf header mismatch / absent) */
const SESSION_REVOKED_I18N_KEYS: ReadonlySet<string> = new Set([
  "auth.missing_session_token",
  "auth.session_token_invalid",
  "auth.session_revoked",
  "auth.csrf_invalid",
]);

/** Wire `i18n_key` for the single-active-surface reject (policy 2): a
 *  mutating request from a DISPLACED/older surface — its `x-surface-id`
 *  no longer owns the active WS slot — is refused with this reason
 *  (`session.superseded`, 403/409). This is a SURFACE fact, not an
 *  account revoke: a newer surface for the SAME account claimed the
 *  slot. Route it to `markSurfaceDisplaced()` (halt this surface +
 *  latch the take-over notice), NOT the logout cascade — the prior
 *  code lumped it into the revoke set and tore down the account
 *  session, killing the winning surface's shared cookie + IDB row. */
const SURFACE_SUPERSEDED_I18N_KEY = "session.superseded";

export type AuthedHttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** Header map a `signHeaders` provider returns each attempt. All
 *  members are optional + string-valued so the route-auth triple
 *  (`PlayerAuthHeaders`), the account-self triple
 *  (`AccountSelfAuthHeaders`), and a caller-minted `Idempotency-Key`
 *  all satisfy it without coupling this module to those interfaces.
 *  Absent (`undefined`) members are skipped when merging. */
export interface SignedAuthHeaders {
  readonly "x-player-id"?: string;
  readonly "x-player-sig"?: string;
  readonly "x-request-ts"?: string;
  readonly "x-signing-cred"?: string;
  readonly "x-ml-dsa-epoch"?: string;
  readonly "x-ml-dsa-sig"?: string;
  readonly "Idempotency-Key"?: string;
}

export interface AuthedRequestArgs {
  /** Absolute URL (caller composes `${serverBase}/path`). */
  readonly url: string;
  /** Default `GET`. Anything other than GET/HEAD is treated as
   *  mutating and gets the CSRF header. */
  readonly method?: AuthedHttpMethod;
  /** Extra headers — merged on top of the helper's defaults
   *  (content-type stays caller-driven so a JSON POST and a
   *  multipart upload can coexist). Static headers are sent on
   *  every attempt verbatim; for any header that must be FRESH per
   *  attempt (a single-use signature, an idempotency key) use
   *  `signHeaders` instead — its output supersedes any same-named
   *  key here on each attempt. */
  readonly headers?: Record<string, string>;
  /** Per-attempt signed-header provider. Called once at the START of
   *  every attempt — the initial request AND each 429 retry — so a
   *  retry never replays a captured single-use signature: each call
   *  produces a fresh wall-clock timestamp + a fresh ed25519 (and any
   *  hybrid ml-dsa) signature, plus a fresh `Idempotency-Key` where
   *  the caller mints one. The returned map is merged ON TOP of the
   *  static `headers` for that attempt, so the freshly-signed
   *  `x-player-{id,sig,ts}` (or account-self) triple overrides any
   *  stale copy a caller left in `headers`.
   *
   *  Security: each invocation is a genuine, freshly-signed request
   *  from the client's own key — NOT a re-use of prior bytes. The
   *  server's single-use replay guard still rejects any captured
   *  replay (same signature + no / mismatched idempotency key). May
   *  be async so a future credential refresh can await. */
  readonly signHeaders?: () => SignedAuthHeaders | Promise<SignedAuthHeaders>;
  /** Raw body — caller serializes (the helper stays codec-
   *  agnostic so binary uploads, JSON, or form-encoded all work). */
  readonly body?: BodyInit;
  /** Vitest injection point — tests pass a stub so the unit
   *  suite never touches a live network. */
  readonly fetchImpl?: typeof fetch;
  /** Abandons the request — a caller's timeout, or an answer nobody
   *  wants any more. */
  readonly signal?: AbortSignal;
}

/** Mutating verbs need the CSRF header echoed back. `GET` and
 *  `HEAD` always skip — matches the BE's
 *  `is_mutating` gate (routes/mod.rs). */
function isMutatingMethod(method: AuthedHttpMethod): boolean {
  return method !== "GET";
}

/** Probe the response body for the BE's `i18n_key` discriminator
 *  without consuming the original `Response` — clones first so
 *  the caller's `.json()` / `.text()` still works. Failures
 *  collapse to `null` so the caller's normal error path runs. */
async function parseReasonKey(response: Response): Promise<string | null> {
  try {
    const cloned = response.clone();
    const body = (await cloned.json()) as { i18n_key?: unknown };
    if (typeof body === "object" && body !== null && "i18n_key" in body) {
      const key = body.i18n_key;
      if (typeof key === "string") return key;
    }
  } catch {
    // Non-JSON or aborted body — fall through.
  }
  return null;
}

/** Thrown synchronously by `authedRequest` when the supplied
 *  `body` is a `ReadableStream` — streams cannot be re-played
 *  after a 429 + retry. Defensive guard for future stream-body
 *  callers; the current FE always passes string / Uint8Array
 *  / URLSearchParams / FormData. */
export class RetryBodyUnsupported extends Error {
  constructor() {
    super(
      "authedRequest: ReadableStream body cannot be replayed across 429 retries",
    );
    this.name = "RetryBodyUnsupported";
  }
}

/** Total retry attempts on `429` before surfacing the response
 *  to the caller. `MAX_RETRY_ATTEMPTS = 3` matches the
 *  global poker-room convention — short enough to avoid wedging
 *  the UI on a sustained rate-limit, long enough to absorb
 *  a single burst. */
const MAX_RETRY_ATTEMPTS = 3;

/** Exponential-backoff base (ms). `delay = min(BASE × 2^attempt,
 *  Retry-After × 1000) × jitter`. 500ms is the
 *  PokerStars / GGPoker published default. */
const BACKOFF_BASE_MS = 500;

/** Parse the `Retry-After` response header (RFC 7231 §7.1.3 +
 *  RFC 6585 §4). Honours both `delta-seconds` (integer string)
 *  + `HTTP-date` (RFC 7231). Returns the delay in milliseconds.
 *  Defaults to 1 s when the header is absent or unparseable so
 *  the caller never spin-retries instantly on a malformed BE. */
function parseRetryAfterMs(headerValue: string | null): number {
  if (headerValue === null) return 1000;
  const asSeconds = Number.parseInt(headerValue, 10);
  if (!Number.isNaN(asSeconds) && asSeconds >= 0) {
    return asSeconds * 1000;
  }
  const asDate = Date.parse(headerValue);
  if (!Number.isNaN(asDate)) {
    const delta = asDate - Date.now();
    return delta > 0 ? delta : 0;
  }
  return 1000;
}

/** Sample a jitter multiplier in `[0.8, 1.2]` so a colliding
 *  burst of clients does not re-flood the gate in lockstep
 *  after the same retry delay. Standard exponential-backoff
 *  decorrelation pattern. */
function jitterMultiplier(): number {
  return 0.8 + Math.random() * 0.4;
}

/** Compute the post-jitter delay (ms) for a given retry
 *  attempt. The cap is `Retry-After × 1000` — the BE is
 *  authoritative on the minimum wait; the exponential schedule
 *  is the soft ceiling for the case where the BE omits the
 *  header. */
function backoffDelayMs(attempt: number, retryAfterMs: number): number {
  const exponential = BACKOFF_BASE_MS * Math.pow(2, attempt);
  const capped = Math.min(exponential, retryAfterMs);
  return Math.max(0, Math.floor(capped * jitterMultiplier()));
}

/** Body classes safe to re-play across a 429 retry. The
 *  `ReadableStream` case has to throw synchronously because
 *  the stream's consumer side is one-shot. */
function assertBodyReplayable(body: BodyInit | undefined): void {
  if (body === undefined) return;
  // `ReadableStream` is the only one-shot body class fetch
  // accepts. Detect by `instanceof` so the test injection
  // path's mocked stream is also caught.
  if (typeof ReadableStream !== "undefined" && body instanceof ReadableStream) {
    throw new RetryBodyUnsupported();
  }
}

export async function authedRequest(
  args: AuthedRequestArgs,
): Promise<TracedResponse> {
  assertBodyReplayable(args.body);
  const fetchImpl = reachable(args.fetchImpl ?? fetch);
  const method: AuthedHttpMethod = args.method ?? "GET";
  let attempt = 0;
  let response: Response;
  // Retry loop — sustains MAX_RETRY_ATTEMPTS rounds on 429
  // before surfacing the final response. The auth headers /
  // CSRF / cookie are re-read on every iteration so a CSRF
  // rotation that landed mid-backoff actually takes effect on
  // the retry attempt. When a `signHeaders` provider is present
  // it runs at the START of every attempt so each request (initial
  // + each retry) carries a FRESH single-use signature + idempotency
  // key (a fresh `x-request-nonce` => a genuinely new signature) — a
  // retry never replays the prior attempt's signed bytes.
  loop: {
    while (true) {
      const headers: Record<string, string> = { ...(args.headers ?? {}) };
      // Policy 1 — min-client-version gate. Stamp the build/protocol
      // version (`"<major>.<minor>"`, the format the BE
      // `client_version::parse_version_str` expects) on every authed
      // request so the server can reject a stale client with `426
      // Upgrade Required`. Set AFTER the caller `headers` spread but
      // BEFORE `signHeaders` so a future signed override could win;
      // today no signer touches it. The current build always reports a
      // version at/above the gate minimum (pinned in
      // `types/wire/protocol.ts`), so it never locks itself out.
      headers["x-client-version"] = CLIENT_VERSION_STRING;
      if (args.signHeaders !== undefined) {
        const signed = await args.signHeaders();
        for (const [k, v] of Object.entries(signed)) {
          if (v !== undefined) headers[k] = v;
        }
      }
      if (isMutatingMethod(method)) {
        const csrf = useAuthStore.getState().csrfToken;
        if (csrf !== null && csrf !== "") {
          headers["x-csrf-token"] = csrf;
        }
        // Policy 2 — single active surface. Echo the opaque surface
        // token the WS handshake's `auth_ok` minted for the active
        // table slot so the BE can verify this mutation rides the
        // surface that currently owns the `(table, player)` WS slot. A
        // displaced/older surface keeps echoing its now-superseded
        // token → BE `403 session.superseded`. Absent (no live slot /
        // reconnect window / pre-policy-2 server) → no header, which
        // the BE gate treats as the back-compat allow case (so a
        // normal single-surface session + a legitimate reconnect are
        // never blocked). Read per-table from the active membership so
        // a token from a different table never leaks across.
        const tableIdHex = useAuthStore.getState().tableIdHex;
        if (tableIdHex !== null) {
          const surface = getSurfaceToken(tableIdHex);
          if (surface !== null) headers["x-surface-id"] = surface;
        }
      }
      const init: RequestInit = {
        method,
        credentials: "include",
        headers,
      };
      if (args.body !== undefined) init.body = args.body;
      if (args.signal !== undefined) init.signal = args.signal;
      response = await fetchImpl(args.url, init);
      if (response.status !== 429 || attempt >= MAX_RETRY_ATTEMPTS) {
        break loop;
      }
      const retryAfterMs = parseRetryAfterMs(
        response.headers.get("retry-after"),
      );
      const delay = backoffDelayMs(attempt, retryAfterMs);
      await new Promise<void>((r) => setTimeout(r, delay));
      attempt += 1;
    }
  }
  if (response.status === 426) {
    // Policy 1 — min-client-version gate rejected this build. Latch
    // the eager upgrade-required flag so the App-level gate renders
    // the blocking "update required — please refresh" surface. This is
    // additive: the caller still receives the raw response so its
    // existing error pipeline can render `error.upgrade_required` via
    // `formatServerError` (the BE envelope carries that i18n_key). NOT
    // a logout — the user's session is fine; the client is just stale.
    useUIStore.getState().markUpgradeRequired();
  }
  // Session-revoke / single-active-surface trigger. The four
  // canonical revoke reasons land on 401/403; the policy-2
  // active-elsewhere reason (a DISPLACED/older surface mutating after
  // a newer surface claimed the active slot) may land on 409 Conflict
  // as well, so the gate includes 409 — the 401/403-only reasons never
  // appear on a 409, so their behaviour is unchanged.
  if (
    response.status === 401 ||
    response.status === 403 ||
    response.status === 409
  ) {
    const reasonKey = await parseReasonKey(response);
    if (reasonKey !== null && SESSION_REVOKED_I18N_KEYS.has(reasonKey)) {
      // Account-revoke reasons — the WS `session_revoked` event + this
      // HTTP path + the cross-tab BroadcastChannel all funnel through
      // `markSessionRevoked()`, which idempotency-guards against
      // repeated entry. The caller still receives the raw response so
      // its existing error pipeline (toast / form state) runs in
      // parallel.
      useAuthStore.getState().markSessionRevoked(reasonKey);
    } else if (reasonKey === SURFACE_SUPERSEDED_I18N_KEY) {
      // Single-active-surface reject — halt THIS surface only (NOT a
      // logout). Mirrors the WS `displaced` frame's surface signal so
      // the App-level take-over modal mounts + the mounted Game effect
      // tears down the WS. The account session stays intact.
      useAuthStore.getState().markSurfaceDisplaced(reasonKey);
    }
  }
  return attachTraceId(response);
}
